// SPDX-License-Identifier: AGPL-3.0-only

import { buildSourceRateGrid, gridMetrics } from '../grid';
import { AdaptiveSimulationDomain, type SimulationDomainWindow } from '../AdaptiveSimulationDomain';
import {
  DEFAULT_SOLVER_PARAMETERS,
  type DemGrid,
  type InflowSource,
  type SimulationSnapshot,
  type SimulationStatistics,
  type SolverParameters,
  validateDemGrid
} from '../types';
import { FLUX_FRAGMENT_SHADER, FULLSCREEN_VERTEX_SHADER, UPDATE_FRAGMENT_SHADER } from './shaders';

interface ProgramBundle {
  readonly program: WebGLProgram;
  readonly uniforms: Record<string, WebGLUniformLocation | null>;
}

export class WebGLShallowWaterSolver {
  readonly grid: DemGrid;
  readonly parameters: SolverParameters;
  readonly metrics: ReturnType<typeof gridMetrics>;

  private readonly gl: WebGL2RenderingContext;
  private readonly terrainTexture: WebGLTexture;
  private readonly sourceTexture: WebGLTexture;
  private readonly stateTextures: readonly [WebGLTexture, WebGLTexture];
  private readonly stateFramebuffers: readonly [WebGLFramebuffer, WebGLFramebuffer];
  private readonly fluxTextures: readonly [WebGLTexture, WebGLTexture];
  private readonly fluxFramebuffers: readonly [WebGLFramebuffer, WebGLFramebuffer];
  private readonly fluxProgram: ProgramBundle;
  private readonly updateProgram: ProgramBundle;
  private readonly vertexArray: WebGLVertexArrayObject;
  private readonly domain: AdaptiveSimulationDomain;
  private sourceRate: Float32Array;
  private stateIndex: 0 | 1 = 0;
  private fluxIndex: 0 | 1 = 0;
  private elapsedSeconds = 0;
  private iteration = 0;

  constructor(
    gl: WebGL2RenderingContext,
    grid: DemGrid,
    parameters: Partial<SolverParameters> = {}
  ) {
    validateDemGrid(grid);
    if (!gl.getExtension('EXT_color_buffer_float')) {
      throw new Error('WebGL2 EXT_color_buffer_float is required.');
    }
    this.gl = gl;
    this.grid = grid;
    this.parameters = { ...DEFAULT_SOLVER_PARAMETERS, ...parameters };
    this.metrics = gridMetrics(grid);
    this.sourceRate = new Float32Array(grid.width * grid.height);
    this.domain = new AdaptiveSimulationDomain(grid.width, grid.height, {
      enabled: this.parameters.adaptiveDomainEnabled,
      tileSizeCells: this.parameters.adaptiveDomainTileSizeCells,
      initialPaddingCells: this.parameters.adaptiveDomainInitialPaddingCells,
      edgeMarginCells: this.parameters.adaptiveDomainEdgeMarginCells,
      wetDepthThresholdMeters: this.parameters.minimumWetDepthMeters
    });
    this.vertexArray = requireResource(gl.createVertexArray(), 'vertex array');
    this.terrainTexture = this.createScalarTexture(grid.elevations);
    this.sourceTexture = this.createScalarTexture(new Float32Array(grid.width * grid.height));
    this.stateTextures = [this.createStateTexture(), this.createStateTexture()];
    this.stateFramebuffers = [
      this.createFramebuffer(this.stateTextures[0]),
      this.createFramebuffer(this.stateTextures[1])
    ];
    this.fluxTextures = [this.createStateTexture(), this.createStateTexture()];
    this.fluxFramebuffers = [
      this.createFramebuffer(this.fluxTextures[0]),
      this.createFramebuffer(this.fluxTextures[1])
    ];
    this.fluxProgram = this.createProgram(FLUX_FRAGMENT_SHADER, [
      'u_terrain',
      'u_state',
      'u_previous_flux',
      'u_size',
      'u_domain',
      'u_cell_size',
      'u_cell_area',
      'u_dt',
      'u_gravity',
      'u_roughness',
      'u_flux_damping',
      'u_pipe_area',
      'u_minimum_depth'
    ]);
    this.updateProgram = this.createProgram(UPDATE_FRAGMENT_SHADER, [
      'u_state',
      'u_flux',
      'u_source_rate',
      'u_size',
      'u_domain',
      'u_cell_size',
      'u_cell_area',
      'u_dt',
      'u_maximum_depth',
      'u_boundary_drain'
    ]);
    this.reset();
  }

  get currentStateTexture(): WebGLTexture {
    return this.stateTextures[this.stateIndex];
  }

  get currentFluxTexture(): WebGLTexture {
    return this.fluxTextures[this.fluxIndex];
  }

  get currentTerrainTexture(): WebGLTexture {
    return this.terrainTexture;
  }

  get modelTimeSeconds(): number {
    return this.elapsedSeconds;
  }

  get activeDomain(): SimulationDomainWindow {
    return this.domain.window;
  }

  setSources(sources: readonly InflowSource[]): void {
    this.sourceRate = buildSourceRateGrid(this.grid, sources);
    const hasSource = this.sourceRate.some((value) => value > 0);
    if (hasSource && this.iteration === 0) this.domain.resetToMask(this.sourceRate);
    else if (hasSource) this.domain.includeMask(this.sourceRate);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      this.grid.width,
      this.grid.height,
      gl.RED,
      gl.FLOAT,
      this.sourceRate
    );
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  reset(): void {
    const gl = this.gl;
    const zero = new Float32Array(this.grid.width * this.grid.height * 4);
    for (const texture of this.stateTextures) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texSubImage2D(
        gl.TEXTURE_2D,
        0,
        0,
        0,
        this.grid.width,
        this.grid.height,
        gl.RGBA,
        gl.FLOAT,
        zero
      );
    }
    for (const texture of this.fluxTextures) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texSubImage2D(
        gl.TEXTURE_2D,
        0,
        0,
        0,
        this.grid.width,
        this.grid.height,
        gl.RGBA,
        gl.FLOAT,
        zero
      );
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.stateIndex = 0;
    this.fluxIndex = 0;
    this.elapsedSeconds = 0;
    this.iteration = 0;
    this.domain.resetToMask(this.sourceRate);
  }

  step(iterations = 1, requestedTimeStepSeconds?: number): number {
    const gl = this.gl;
    const dt = requestedTimeStepSeconds ?? this.stableTimeStep();
    if (!(dt > 0) || !Number.isFinite(dt)) {
      throw new Error('Time step must be finite and positive.');
    }
    gl.bindVertexArray(this.vertexArray);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    for (let index = 0; index < iterations; index += 1) {
      const domain = this.domain.window;
      gl.viewport(domain.x, domain.y, domain.width, domain.height);
      this.renderFlux(dt);
      this.renderUpdate(dt);
      this.stateIndex = this.stateIndex === 0 ? 1 : 0;
      this.fluxIndex = this.fluxIndex === 0 ? 1 : 0;
      this.elapsedSeconds += dt;
      this.iteration += 1;
      const checkInterval = Math.max(1, Math.round(this.parameters.adaptiveDomainCheckInterval));
      if (this.iteration % checkInterval === 0) this.expandDomainFromCurrentState();
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindVertexArray(null);
    gl.useProgram(null);
    return dt * iterations;
  }

  stableTimeStep(): number {
    const cellSize = 0.5 * (this.metrics.cellWidthMeters + this.metrics.cellHeightMeters);
    return Math.max(
      0.005,
      Math.min(this.parameters.maxTimeStepSeconds, 0.015 * Math.sqrt(Math.max(cellSize, 0.1) / 5))
    );
  }

  snapshot(): SimulationSnapshot {
    const state = this.readState();
    const depth = new Float32Array(this.grid.width * this.grid.height);
    const velocityEast = new Float32Array(depth.length);
    const velocityNorth = new Float32Array(depth.length);
    for (let index = 0; index < depth.length; index += 1) {
      depth[index] = state[index * 4] ?? 0;
      velocityEast[index] = state[index * 4 + 1] ?? 0;
      velocityNorth[index] = state[index * 4 + 2] ?? 0;
    }
    return {
      depth,
      velocityEast,
      velocityNorth,
      statistics: this.statisticsFromDepth(depth)
    };
  }

  statistics(): SimulationStatistics {
    return this.snapshot().statistics;
  }

  destroy(): void {
    const gl = this.gl;
    gl.deleteTexture(this.terrainTexture);
    gl.deleteTexture(this.sourceTexture);
    for (const texture of this.stateTextures) gl.deleteTexture(texture);
    for (const framebuffer of this.stateFramebuffers) gl.deleteFramebuffer(framebuffer);
    for (const texture of this.fluxTextures) gl.deleteTexture(texture);
    for (const framebuffer of this.fluxFramebuffers) gl.deleteFramebuffer(framebuffer);
    gl.deleteProgram(this.fluxProgram.program);
    gl.deleteProgram(this.updateProgram.program);
    gl.deleteVertexArray(this.vertexArray);
  }

  private renderFlux(dt: number): void {
    const gl = this.gl;
    const bundle = this.fluxProgram;
    const nextFluxIndex = this.fluxIndex === 0 ? 1 : 0;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fluxFramebuffers[nextFluxIndex]);
    gl.useProgram(bundle.program);
    this.bindTexture(0, this.terrainTexture, uniform(bundle, 'u_terrain'));
    this.bindTexture(1, this.currentStateTexture, uniform(bundle, 'u_state'));
    this.bindTexture(2, this.currentFluxTexture, uniform(bundle, 'u_previous_flux'));
    gl.uniform2i(uniform(bundle, 'u_size'), this.grid.width, this.grid.height);
    this.bindDomainUniform(bundle);
    gl.uniform2f(
      uniform(bundle, 'u_cell_size'),
      this.metrics.cellWidthMeters,
      this.metrics.cellHeightMeters
    );
    gl.uniform1f(uniform(bundle, 'u_cell_area'), this.metrics.cellAreaSquareMeters);
    gl.uniform1f(uniform(bundle, 'u_dt'), dt);
    gl.uniform1f(uniform(bundle, 'u_gravity'), this.parameters.gravity);
    gl.uniform1f(uniform(bundle, 'u_roughness'), this.parameters.roughness);
    gl.uniform1f(uniform(bundle, 'u_flux_damping'), this.parameters.fluxDamping);
    gl.uniform1f(
      uniform(bundle, 'u_pipe_area'),
      Math.max(
        0.08,
        this.parameters.pipeAreaScale *
          Math.min(this.metrics.cellWidthMeters, this.metrics.cellHeightMeters)
      )
    );
    gl.uniform1f(uniform(bundle, 'u_minimum_depth'), this.parameters.minimumWetDepthMeters);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  private renderUpdate(dt: number): void {
    const gl = this.gl;
    const nextIndex = this.stateIndex === 0 ? 1 : 0;
    const bundle = this.updateProgram;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.stateFramebuffers[nextIndex]);
    gl.useProgram(bundle.program);
    this.bindTexture(0, this.currentStateTexture, uniform(bundle, 'u_state'));
    const nextFluxIndex = this.fluxIndex === 0 ? 1 : 0;
    this.bindTexture(1, this.fluxTextures[nextFluxIndex], uniform(bundle, 'u_flux'));
    this.bindTexture(2, this.sourceTexture, uniform(bundle, 'u_source_rate'));
    gl.uniform2i(uniform(bundle, 'u_size'), this.grid.width, this.grid.height);
    this.bindDomainUniform(bundle);
    gl.uniform2f(
      uniform(bundle, 'u_cell_size'),
      this.metrics.cellWidthMeters,
      this.metrics.cellHeightMeters
    );
    gl.uniform1f(uniform(bundle, 'u_cell_area'), this.metrics.cellAreaSquareMeters);
    gl.uniform1f(uniform(bundle, 'u_dt'), dt);
    gl.uniform1f(uniform(bundle, 'u_maximum_depth'), this.parameters.maximumDepthMeters);
    gl.uniform1f(uniform(bundle, 'u_boundary_drain'), this.parameters.openBoundaryDrainRate);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  private bindTexture(
    unit: number,
    texture: WebGLTexture,
    location: WebGLUniformLocation | null | undefined
  ): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    if (location) gl.uniform1i(location, unit);
  }

  private bindDomainUniform(bundle: ProgramBundle): void {
    const domain = this.domain.window;
    this.gl.uniform4i(
      uniform(bundle, 'u_domain'),
      domain.x,
      domain.y,
      domain.x + domain.width - 1,
      domain.y + domain.height - 1
    );
  }

  private createScalarTexture(data: Float32Array): WebGLTexture {
    const gl = this.gl;
    const texture = requireResource(gl.createTexture(), 'scalar texture');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    this.configureTexture();
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.R32F,
      this.grid.width,
      this.grid.height,
      0,
      gl.RED,
      gl.FLOAT,
      data
    );
    return texture;
  }

  private createStateTexture(): WebGLTexture {
    const gl = this.gl;
    const texture = requireResource(gl.createTexture(), 'state texture');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    this.configureTexture();
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      this.grid.width,
      this.grid.height,
      0,
      gl.RGBA,
      gl.FLOAT,
      null
    );
    return texture;
  }

  private configureTexture(): void {
    const gl = this.gl;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  private createFramebuffer(texture: WebGLTexture): WebGLFramebuffer {
    const gl = this.gl;
    const framebuffer = requireResource(gl.createFramebuffer(), 'framebuffer');
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error('Could not create a complete float framebuffer.');
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return framebuffer;
  }

  private createProgram(fragmentSource: string, uniformNames: readonly string[]): ProgramBundle {
    const gl = this.gl;
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, FULLSCREEN_VERTEX_SHADER);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    const program = requireResource(gl.createProgram(), 'program');
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const message = gl.getProgramInfoLog(program) || 'Unknown program link error.';
      gl.deleteProgram(program);
      throw new Error(message);
    }
    const uniforms: Record<string, WebGLUniformLocation | null> = {};
    for (const name of uniformNames) uniforms[name] = gl.getUniformLocation(program, name);
    return { program, uniforms };
  }

  private readState(): Float32Array {
    const gl = this.gl;
    const data = new Float32Array(this.grid.width * this.grid.height * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.stateFramebuffers[this.stateIndex]);
    gl.readPixels(0, 0, this.grid.width, this.grid.height, gl.RGBA, gl.FLOAT, data);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return data;
  }

  private expandDomainFromCurrentState(): void {
    const domain = this.domain.window;
    if (domain.activeCellCount === domain.totalCellCount) return;
    const margin = Math.max(1, Math.round(this.parameters.adaptiveDomainEdgeMarginCells));
    const horizontalMargin = Math.min(margin + 1, domain.width);
    const verticalMargin = Math.min(margin + 1, domain.height);
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.stateFramebuffers[this.stateIndex]);
    const west = this.regionContainsWetDepth(domain.x, domain.y, horizontalMargin, domain.height);
    const east = this.regionContainsWetDepth(
      domain.x + domain.width - horizontalMargin,
      domain.y,
      horizontalMargin,
      domain.height
    );
    const north = this.regionContainsWetDepth(domain.x, domain.y, domain.width, verticalMargin);
    const south = this.regionContainsWetDepth(
      domain.x,
      domain.y + domain.height - verticalMargin,
      domain.width,
      verticalMargin
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.domain.expandEdges({ west, north, east, south });
  }

  private regionContainsWetDepth(x: number, y: number, width: number, height: number): boolean {
    const state = new Float32Array(width * height * 4);
    this.gl.readPixels(x, y, width, height, this.gl.RGBA, this.gl.FLOAT, state);
    for (let index = 0; index < state.length; index += 4) {
      if ((state[index] ?? 0) > this.parameters.minimumWetDepthMeters) return true;
    }
    return false;
  }

  private statisticsFromDepth(depth: Float32Array): SimulationStatistics {
    let wetCellCount = 0;
    let maximumDepthMeters = 0;
    let wetDepthSum = 0;
    let waterVolumeCubicMeters = 0;
    for (const value of depth) {
      maximumDepthMeters = Math.max(maximumDepthMeters, value);
      waterVolumeCubicMeters += value * this.metrics.cellAreaSquareMeters;
      if (value >= this.parameters.minimumWetDepthMeters) {
        wetCellCount += 1;
        wetDepthSum += value;
      }
    }
    return {
      elapsedSeconds: this.elapsedSeconds,
      iteration: this.iteration,
      wetCellCount,
      maximumDepthMeters,
      meanWetDepthMeters: wetCellCount === 0 ? 0 : wetDepthSum / wetCellCount,
      waterVolumeCubicMeters
    };
  }
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = requireResource(gl.createShader(type), 'shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Unknown shader compilation error.';
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function requireResource<T>(resource: T | null, label: string): T {
  if (!resource) throw new Error(`Could not allocate WebGL ${label}.`);
  return resource;
}

function uniform(bundle: ProgramBundle, name: string): WebGLUniformLocation {
  const location = bundle.uniforms[name];
  if (!location) throw new Error(`WebGL program is missing uniform ${name}.`);
  return location;
}
